// issue #185 (S8 landing): the robot routes (/, /robot, /robot-config, /ai)
// render OUTSIDE DashboardLayout, so they inherit neither
// LayoutScrollRestoration (POP restore) nor the dashboard tree's
// armBootScrollGuard. On a refresh (POP) or browser back/forward into the
// landing, Chrome re-applies the pre-refresh scrollY before React paints and
// the operator lands no meio do documento (IA Core some atrás do histórico).
//
// ScrollRestorationToTop at the AppShell root only resets PUSH/REPLACE — it
// deliberately skips POP so the dashboard tree can restore offsets. The robot
// landing has no offset worth restoring: the operator must always see IA Core
// at the top. This guard mounts at the robot ROUTE GROUP level (a layout
// route wrapping the robot children in routes.partial.ts), never inside
// RobotPage, and snaps scrollY to 0 for EVERY navigation type — including the
// POP that a refresh/back produces — re-asserting across a few frames to
// defeat Chrome's late boot scroll-restore + scroll-anchoring heuristics.
//
// While mounted it also flips history.scrollRestoration to "manual" so the
// browser's native POP scroll-restore (refresh/back) does not fight the
// snap-to-top — and restores the previous value (default "auto") on unmount,
// so the dashboard tree keeps relying on native restore for its own
// full-document back navigation (C51-SCROLL-RESTORE-CROSS-ROUTE, which uses
// page.goto/goBack across fresh documents where the in-memory
// LayoutScrollRestoration map is empty).
//
// Contract:
//   • Enter /robot via login PUSH → scrollY 0 before first paint.
//   • Refresh /robot (POP) → scrollY 0; IA Core panel above the history.
//   • Back/forward into a robot route (POP) → scrollY 0.
//   • Leaving for a dashboard route unmounts this guard, restoring "auto" so
//     the dashboard tree's native + LayoutScrollRestoration back/forward
//     restore keeps working — we never reset scroll on routes we don't
//     render under.
import { useLayoutEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

const REASSERT_FRAMES = 3;

export function RobotLandingScrollGuard(): React.JSX.Element {
  const { pathname } = useLocation();
  useScrollRestorationManualWhileMounted();
  useLayoutEffect(() => snapToTopAcrossFrames(), [pathname]);
  return <Outlet />;
}

// Robot routes own their scroll: pin scrollRestoration to "manual" so neither
// the browser nor React Router replays a stale offset over the landing. Scope
// it to this route group only — the dashboard tree needs native "auto".
function useScrollRestorationManualWhileMounted(): void {
  useLayoutEffect(() => {
    if (typeof history === "undefined" || !("scrollRestoration" in history)) {
      return undefined;
    }
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);
}

// useLayoutEffect commits the first reset in the same paint as the route's
// first render; the rAF chain re-asserts because Chrome's boot scroll-restore
// (and scroll-anchoring when the SplashLoader unmounts above the fold) can
// fire a few frames later through a path that bypasses window.scrollTo.
function snapToTopAcrossFrames(): () => void {
  if (typeof window === "undefined") return () => undefined;
  let frame = 0;
  let rafId = 0;
  const snap = (): void => {
    window.scrollTo(0, 0);
    frame += 1;
    if (frame < REASSERT_FRAMES) rafId = window.requestAnimationFrame(snap);
  };
  snap();
  return () => window.cancelAnimationFrame(rafId);
}
